import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppConfirmService } from 'app/shared/services/app-confirm/app-confirm.service';
import { AppLoaderService } from 'app/shared/services/app-loader/app-loader.service';
import {
  AtributoAnuncio,
  MercadoLivreAnuncioService,
  ProdutoAnuncio,
  SaldoCanal,
  SugestaoCategoria,
  ValidacaoAnuncio
} from 'app/shared/services/mercado-livre-anuncio.service';

/**
 * Mercado Livre dentro do cadastro de produto.
 *
 * Fica aqui, e não numa tela própria, porque tudo que a publicação exige é cadastro: categoria,
 * atributos, foto, dimensões. Corrigir o que falta a três telas de distância é o que faz esse tipo
 * de integração emperrar.
 *
 * A ordem da tela é a ordem do trabalho: mapear → preencher o que a categoria exige → conferir →
 * publicar.
 */
@Component({
  selector: 'app-produto-mercado-livre',
  templateUrl: './produto-mercado-livre.component.html',
  styleUrls: ['./produto-mercado-livre.component.scss']
})
export class ProdutoMercadoLivreComponent implements OnChanges {

  /** Produto exibido. Enquanto não existe (produto novo), a seção nem aparece. */
  @Input() produtoId: number;

  anuncio: ProdutoAnuncio = null;
  validacao: ValidacaoAnuncio = null;
  saldo: SaldoCanal = null;
  atributos: AtributoAnuncio[] = [];
  sugestoes: SugestaoCategoria[] = [];

  carregando = false;
  publicando = false;
  mostrarTodosAtributos = false;

  /** Campo do vínculo manual. */
  itemIdInformado = '';

  /**
   * Sugestões do ML por atributo, carregadas sob demanda.
   *
   * Chave é o código do atributo (BRAND, MODEL). Carregar todas de uma vez seria uma chamada por
   * atributo ao abrir a tela — a maioria nunca consultada.
   */
  sugestoesPorAtributo: { [code: string]: string[] } = {};

  /** Escolhas da preparação de um anúncio novo. */
  categoriaEscolhida = '';
  listingTypeId = 'gold_special';
  condicao = 'new';

  tiposAnuncio = [
    { valor: 'gold_special', rotulo: 'Clássico' },
    { valor: 'gold_pro', rotulo: 'Premium' },
    { valor: 'free', rotulo: 'Grátis' }
  ];

  condicoes = [
    { valor: 'new', rotulo: 'Novo' },
    { valor: 'used', rotulo: 'Usado' },
    { valor: 'not_specified', rotulo: 'Não especificado' }
  ];

  constructor(
    private anuncioService: MercadoLivreAnuncioService,
    private snack: MatSnackBar,
    private loader: AppLoaderService,
    private confirmService: AppConfirmService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['produtoId'] && this.produtoId) {
      this.carregar();
    }
  }

  get mapeado(): boolean {
    return !!this.anuncio;
  }

  get publicado(): boolean {
    return !!this.anuncio?.itemId;
  }

  /**
   * Estado da seção.
   *
   * O 204 do mapeamento inexistente chega como corpo nulo — é estado normal (produto ainda não
   * mapeado), não erro, e por isso não vira mensagem vermelha.
   */
  carregar() {
    if (!this.produtoId) {
      return;
    }
    this.carregando = true;
    this.anuncioService.getAnuncio(this.produtoId).subscribe(
      anuncio => {
        this.anuncio = anuncio || null;
        this.carregando = false;
        if (this.anuncio) {
          this.carregarDependentes();
        }
      },
      () => {
        this.anuncio = null;
        this.carregando = false;
      });
    this.carregarSaldo();
  }

  /** Validação e atributos só fazem sentido depois que há categoria escolhida. */
  private carregarDependentes() {
    this.anuncioService.validar(this.produtoId).subscribe(
      validacao => this.validacao = validacao,
      () => this.validacao = null);
    this.carregarAtributos();
  }

  carregarSaldo() {
    this.anuncioService.getSaldo(this.produtoId).subscribe(
      saldo => this.saldo = saldo,
      () => this.saldo = null);
  }

  carregarAtributos() {
    if (!this.anuncio?.categoryId) {
      this.atributos = [];
      return;
    }
    this.anuncioService.getAtributos(this.produtoId, !this.mostrarTodosAtributos).subscribe(
      atributos => this.atributos = atributos || [],
      () => this.atributos = []);
  }

  /**
   * Busca sugestões para um atributo, uma vez só.
   *
   * Disparada ao focar o campo: quem vai digitar marca é quem quer a lista, e quem só olha a tela
   * não gasta chamada nenhuma.
   */
  carregarSugestoes(atributo: AtributoAnuncio) {
    if (!atributo?.code || this.sugestoesPorAtributo[atributo.code]) {
      return;
    }
    this.sugestoesPorAtributo[atributo.code] = [];
    this.anuncioService.getValoresSugeridos(this.produtoId, atributo.code).subscribe(
      valores => this.sugestoesPorAtributo[atributo.code] = valores || [],
      () => this.sugestoesPorAtributo[atributo.code] = []);
  }

  alternarTodosAtributos() {
    this.mostrarTodosAtributos = !this.mostrarTodosAtributos;
    this.carregarAtributos();
  }

  /** Vincula o produto a um anúncio que já existe no ML. */
  vincular() {
    const itemId = (this.itemIdInformado || '').trim();
    if (!itemId) {
      this.snack.open('Informe o código do anúncio (MLB...).', 'OK', { duration: 4000, verticalPosition: 'top' });
      return;
    }
    this.loader.open();
    this.anuncioService.vincular(this.produtoId, itemId).subscribe(
      anuncio => {
        this.loader.close();
        this.anuncio = anuncio;
        this.itemIdInformado = '';
        this.carregarDependentes();
        this.snack.open('Produto vinculado ao anúncio ' + anuncio.itemId, 'OK',
          { duration: 5000, verticalPosition: 'top' });
      },
      err => this.erro(err, 'Não foi possível vincular o anúncio.'));
  }

  sugerirCategorias() {
    this.loader.open();
    this.anuncioService.sugerirCategorias(this.produtoId).subscribe(
      sugestoes => {
        this.loader.close();
        this.sugestoes = sugestoes || [];
        if (!this.sugestoes.length) {
          this.snack.open('Nenhuma sugestão. Informe a categoria manualmente.', 'OK',
            { duration: 5000, verticalPosition: 'top' });
        }
      },
      err => this.erro(err, 'Não foi possível consultar as categorias.'));
  }

  escolherSugestao(sugestao: SugestaoCategoria) {
    this.categoriaEscolhida = sugestao.category_id;
  }

  /** Prepara o mapeamento de um anúncio que ainda vai nascer. */
  preparar() {
    if (!this.categoriaEscolhida) {
      this.snack.open('Escolha a categoria do Mercado Livre.', 'OK', { duration: 4000, verticalPosition: 'top' });
      return;
    }
    this.loader.open();
    this.anuncioService.preparar(this.produtoId, this.categoriaEscolhida, this.listingTypeId, this.condicao)
      .subscribe(
        anuncio => {
          this.loader.close();
          this.anuncio = anuncio;
          this.sugestoes = [];
          this.carregarDependentes();
          this.snack.open('Mapeamento preparado.', 'OK', { duration: 4000, verticalPosition: 'top' });
        },
        err => this.erro(err, 'Não foi possível preparar o mapeamento.'));
  }

  /** Grava os atributos digitados. Os que vieram do cadastro não são reenviados. */
  salvarAtributos() {
    const valores = this.atributos
      .filter(a => a.origem !== 'cadastro')
      .map(a => ({ code: a.code, descricao: a.nome, valor: a.valor || '' }));
    if (!valores.length) {
      return;
    }
    this.loader.open();
    this.anuncioService.salvarAtributos(this.produtoId, valores).subscribe(
      () => {
        this.loader.close();
        this.carregarDependentes();
        this.snack.open('Atributos salvos.', 'OK', { duration: 4000, verticalPosition: 'top' });
      },
      err => this.erro(err, 'Não foi possível salvar os atributos.'));
  }

  /**
   * Publica ou sincroniza.
   *
   * A quantidade vem da pendência LS (remessa), não do estoque — por isso o saldo aparece ao lado
   * do botão: é exatamente o que será enviado.
   */
  publicar() {
    this.publicando = true;
    this.anuncioService.publicar(this.produtoId).subscribe(
      resultado => {
        this.publicando = false;
        if (resultado.sucesso) {
          this.snack.open(`Anúncio ${resultado.itemId} atualizado: ${resultado.qtde} un.`, 'OK',
            { duration: 6000, verticalPosition: 'top' });
          this.carregar();
        } else {
          // Falha de publicação vem como resultado, não como erro HTTP: o motivo é do produto
          // (atributo faltando, sem saldo), não da chamada.
          this.confirmService.confirm({
            title: 'Não publicado',
            message: resultado.mensagem || 'O Mercado Livre recusou o anúncio.',
            error: true
          });
          this.carregar();
        }
      },
      err => {
        this.publicando = false;
        this.erro(err, 'Falha ao publicar.');
      });
  }

  desvincular() {
    this.confirmService.confirm({
      message: 'Remover o vínculo com o anúncio? O anúncio continua no Mercado Livre — '
        + 'o produto é que deixa de atualizá-lo.'
    }).subscribe(res => {
      if (!res) {
        return;
      }
      this.loader.open();
      this.anuncioService.desvincular(this.anuncio.id).subscribe(
        () => {
          this.loader.close();
          this.anuncio = null;
          this.validacao = null;
          this.atributos = [];
          this.snack.open('Vínculo removido.', 'OK', { duration: 4000, verticalPosition: 'top' });
        },
        err => this.erro(err, 'Não foi possível remover o vínculo.'));
    });
  }

  private erro(err: any, padrao: string) {
    this.loader.close();
    this.confirmService.confirm({
      title: 'Erro',
      message: err?.error?.message || err?.error?.error || padrao,
      error: true
    });
  }
}
